import React, { useState } from "react";
import { RegisterFormData } from "../types/RegisterFormData";
import { pageNames } from "../../config/pageNames";

import "./styles/RegisterForm.css";
import { Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

interface RegisterFormProps {
  onSubmit: (formData: RegisterFormData) => void;
  isLoading: boolean;
  errorMessage: string;
}

export const RegisterForm = ({
  onSubmit,
  isLoading,
  errorMessage,
}: RegisterFormProps) => {
  const [formData, setFormData] = useState<RegisterFormData>({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    gdprConfirm: false,
    heardFrom: "",
    heardFromOther: "",
  });

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = event.target;
    const inputValue =
      type === "checkbox"
        ? (event.target as HTMLInputElement).checked
        : value;
  
    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
  };  

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const finalPayload = {
      ...formData,
      finalHeardFrom:
        formData.heardFrom === "Other"
          ? formData.heardFromOther
          : formData.heardFrom,
    };
    onSubmit(finalPayload);
  };

  const renderErrorMessage = (message: string) => {
    const triggerText = "Please sign in";

    if (!message.includes(triggerText)) {
      return <p>{message}</p>;
    }

    const [before, after] = message.split(triggerText);

    return (
      <p>
        {before}
        <Link to={pageNames.LOGIN} className="register-signin__link">
          Sign in
        </Link>
        {after}
      </p>
    );
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <header className="register-header">
          <Row className="align-items-center">
            <Col xs={12} md={6}>
              <h3 className="register-title">Create your account</h3>
              <p className="register-subtitle">
                Start identifying YOUR actions in just a few steps.
              </p>
            </Col>
            <Col
              xs={12}
              md={6}
              className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0"
            >
              <div className="register-signin">
                <span className="register-signin__label">Already have an account?</span>
                <Link to={pageNames.LOGIN} className="register-signin__link">
                  Sign in
                </Link>
              </div>
            </Col>
          </Row>
        </header>

        <form onSubmit={handleSubmit} className="register-form">
          {errorMessage && (
            <div className="register-error">
              <p>{renderErrorMessage(errorMessage)}</p>
            </div>
          )}

          <div className="register-form__grid">
            <div className="form-group">
              <label htmlFor="name">Full name</label>
              <input
                type="text"
                id="name"
                name="name"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g. Alex Johnson"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Work email</label>
              <input
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@company.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter a secure password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Re-enter your password"
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="heardFrom">How did you hear about us?</label>
            <select
              id="heardFrom"
              name="heardFrom"
              value={formData.heardFrom}
              onChange={handleChange}
              required
            >
              <option value="">Select an option</option>
              <option value="Google search">Google search</option>
              <option value="Friend or colleague">Friend or colleague</option>
              <option value="Social media">Social media</option>
              <option value="Sustainability conference">Sustainability conference</option>
              <option value="Newsletter or article">Newsletter or article</option>
              <option value="Other">Other</option>
            </select>

            {formData.heardFrom === "Other" && (
              <input
                type="text"
                name="heardFromOther"
                value={formData.heardFromOther || ""}
                onChange={handleChange}
                placeholder="Please specify..."
                className="mt-2"
                required
              />
            )}
          </div>

          <div className="form-group gdpr-check">
            <div className="gdpr-check__inner">
              <input
                type="checkbox"
                id="chkGdprAgree"
                name="gdprConfirm"
                checked={formData.gdprConfirm}
                onChange={handleChange}
              />
              <label htmlFor="chkGdprAgree" className="gdpr-check__label">
                <span>
                  Please tick this box if you would like to <strong>opt out</strong> of receiving
                  future communications from us. We only send relevant product updates and will
                  never share your data with third parties.
                </span>
              </label>
            </div>
          </div>

          <div className="register-actions">
            <button
              type="submit"
              className={`register-button ${isLoading ? "register-button--loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                "Create account"
              )}
            </button>
            <p className="register-terms">
              By creating an account you agree to our{" "}
              <span className="register-terms__highlight">Terms of Use</span> and{" "}
              <span className="register-terms__highlight">Privacy Policy</span>.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
